import { z } from 'zod';

export const GithubStatusSchema = z.object({
	status: z.enum(['connected', 'expired', 'not_connected']),
	selectedOrgs: z.array(z.string()).optional(),
	selectedRepos: z.array(z.string()).optional(),
});
export type GithubStatus = z.infer<typeof GithubStatusSchema>;

// Admin-only connection state for the org-connector members view. Mirrors the
// per-user connector status shape but is read through the admin's token and
// surfaces the orgs the members list can be scoped to.
export const GithubConnectionSchema = z.object({
	status: z.enum(['connected', 'expired', 'not_connected']),
	selectedOrgs: z.array(z.string()).optional(),
	selectedRepos: z.array(z.string()).optional(),
});
export type GithubConnection = z.infer<typeof GithubConnectionSchema>;

export const GithubAuthorizeSchema = z.object({
	redirectUrl: z.string(),
});
export type GithubAuthorize = z.infer<typeof GithubAuthorizeSchema>;

export const GithubDisconnectSchema = z.object({
	disconnected: z.boolean(),
});
export type GithubDisconnect = z.infer<typeof GithubDisconnectSchema>;

export const GithubRepoSchema = z.object({
	fullName: z.string(),
	name: z.string(),
	owner: z.string(),
	ownerType: z.enum(['User', 'Organization']),
	private: z.boolean(),
	description: z.string().nullable(),
});
export type GithubRepo = z.infer<typeof GithubRepoSchema>;

export const GithubReposResponseSchema = z.object({
	repos: z.array(GithubRepoSchema),
	totalCount: z.number(),
});

export const GithubPreferencesSchema = z.object({
	selectedOrgs: z.array(z.string()),
	selectedRepos: z.array(z.string()),
});
export type GithubPreferences = z.infer<typeof GithubPreferencesSchema>;

// Admin org-member mapping (BIZY-492 backend contract). The `bizy_member`
// payload reuses the per-member identity shape across the members list and the
// confirm-mapping response, so it lives in its own schema.
export const GithubBizyMemberSchema = z.object({
	userId: z.string(),
	memberId: z.string().nullable(),
	// Name/email can be null when the matched member's profile cache is cold or
	// incomplete; fall back to the GitHub login for display.
	name: z.string().nullable(),
	email: z.string().nullable(),
	// Cached profile avatar; null when the cache is cold (fall back to initials).
	avatarUrl: z.string().nullable().optional(),
});
export type GithubBizyMember = z.infer<typeof GithubBizyMemberSchema>;

// `mapped` = durable identity link persisted (done). `suggested` = an OAuth
// self-link auto-identified a Bonusly user, but nothing is persisted yet — the
// admin must confirm it. `unmatched` = no candidate.
export const GithubMatchStatusSchema = z.enum(['mapped', 'suggested', 'unmatched']);
export type GithubMatchStatus = z.infer<typeof GithubMatchStatusSchema>;

// `existing_identity` pairs with `mapped`. `oauth` (high confidence) and
// `login_guess` (lower confidence — the login uniquely matched an enrolled
// member's name/slug) pair with `suggested`. `null` for unmatched.
export const GithubMatchSourceSchema = z.enum(['existing_identity', 'oauth', 'login_guess']).nullable();
export type GithubMatchSource = z.infer<typeof GithubMatchSourceSchema>;

export const GithubOrgMemberSchema = z.object({
	githubId: z.number(),
	login: z.string(),
	avatarUrl: z.string().nullable().optional(),
	type: z.string(),
	matchStatus: GithubMatchStatusSchema,
	matchSource: GithubMatchSourceSchema.optional(),
	bizyMember: GithubBizyMemberSchema.nullable(),
});
export type GithubOrgMember = z.infer<typeof GithubOrgMemberSchema>;

// Single-org, server-paginated members list. There is intentionally no total
// count — paging is driven entirely by `hasMore`.
export const GithubOrgMembersResponseSchema = z.object({
	org: z.string(),
	page: z.number(),
	perPage: z.number(),
	hasMore: z.boolean(),
	members: z.array(GithubOrgMemberSchema),
});
export type GithubOrgMembersResponse = z.infer<typeof GithubOrgMembersResponseSchema>;

export const GithubMappingSchema = z.object({
	githubId: z.number(),
	githubLogin: z.string().nullable().optional(),
	bizyPersonId: z.string(),
	bizyMember: GithubBizyMemberSchema,
});
export type GithubMapping = z.infer<typeof GithubMappingSchema>;

export const GithubMappingRemovalSchema = z.object({
	removed: z.boolean(),
	githubId: z.number(),
});
export type GithubMappingRemoval = z.infer<typeof GithubMappingRemovalSchema>;
